import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { IOrder, DOMAIN_NAMES } from '@cakeiteasy/web-utils';
import { FeatureFlagOptions, FeatureFlagResult } from 'posthog-js';
import { postHogHelper, IPostHogIdentifyPayload } from '../helpers/posthog.helpers';
import { IUser } from '../../users/models/i-user';
import { IAuthTrackPayload, IUserDataPaymentInfo } from '../interfaces';
import { isPostHogInitialized } from './posthog-init.service';
import { getPosthog } from './posthog-client';
import { DINTERO_PAYMENT_FLAG, FEATURE_FLAGS_TIMEOUT_MS } from '../constants';
import { LocationManagerService } from '@cakeiteasy/web-utils';

@Injectable({
  providedIn: 'root',
})
export class PostHogManagerService {
  private _lastIdentifyKey = '';
  private _useDinteroPayment: Promise<boolean>;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private _locationManager: LocationManagerService,
  ) {}

  get posthog() {
    return getPosthog();
  }

  /**
   * Dintero payment experiment: only NO (and localhost) takes part, everyone else keeps Bambora/Klarna.
   * The flag is read only for participants so non-NO visitors are not counted as experiment exposures.
   * Falls back to the old flow when PostHog is unavailable or flags do not load in time.
   * The first decision is kept for the page lifetime: `identify()` during checkout can re-bucket the user,
   * and the A/B payment routes have different child paths, so switching mid-flow would break navigation.
   * It is also kept across reloads (see `_getSavedDinteroGroup`), so a reload never swaps the checkout.
   */
  shouldUseDinteroPayment(): Promise<boolean> {
    this._useDinteroPayment ??= this._resolveDinteroPayment();
    return this._useDinteroPayment;
  }

  private async _resolveDinteroPayment(): Promise<boolean> {
    const host = this._locationManager.getHost()?.toLowerCase();
    const isLocalhost = host?.split(':')[0] === 'localhost';

    if (!isLocalhost && this._locationManager.getDomainName() !== DOMAIN_NAMES.NO) {
      return false;
    }

    // A group saved on an earlier load wins right away: waiting for PostHog again could time out or
    // re-bucket the user and send them to the other checkout. PostHog is still asked, without counting
    // an exposure, so a changed or switched-off flag applies from the next load.
    const savedGroup = this._getSavedDinteroGroup();
    if (savedGroup) {
      this._loadDinteroGroup(false);
      return savedGroup === DINTERO_PAYMENT_FLAG.TEST;
    }

    return (await this._loadDinteroGroup(true)) === DINTERO_PAYMENT_FLAG.TEST;
  }

  /** Read the flag from PostHog and save it for the next load; `undefined` when flags did not load. */
  private async _loadDinteroGroup(countExposure: boolean): Promise<string | undefined> {
    if (!(await this.whenFeatureFlagsLoaded())) {
      return undefined;
    }

    const group = this.getFeatureFlagResult(DINTERO_PAYMENT_FLAG.KEY, { send_event: countExposure })?.variant;
    if (group === DINTERO_PAYMENT_FLAG.TEST || group === DINTERO_PAYMENT_FLAG.CONTROL) {
      this._saveDinteroGroup(group);
    } else {
      // The experiment is over or the flag is off: stop pinning the user to a group.
      this._saveDinteroGroup(null);
    }
    return group;
  }

  private _getSavedDinteroGroup(): string | null {
    try {
      const saved = isPlatformBrowser(this.platformId) && localStorage.getItem(DINTERO_PAYMENT_FLAG.STORAGE_KEY);
      if (!saved) {
        return null;
      }

      const { group, savedAt } = JSON.parse(saved);
      return Date.now() - savedAt < DINTERO_PAYMENT_FLAG.STORAGE_TTL_MS ? group : null;
    } catch {
      return null;
    }
  }

  private _saveDinteroGroup(group: string | null) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      if (group) {
        localStorage.setItem(DINTERO_PAYMENT_FLAG.STORAGE_KEY, JSON.stringify({ group, savedAt: Date.now() }));
      } else {
        localStorage.removeItem(DINTERO_PAYMENT_FLAG.STORAGE_KEY);
      }
    } catch {
      // Storage blocked (private mode, quota): the experiment just falls back to asking PostHog each load.
    }
  }

  whenFeatureFlagsLoaded(timeoutMs = FEATURE_FLAGS_TIMEOUT_MS): Promise<boolean> {
    if (!this.isLoaded()) {
      return Promise.resolve(false);
    }

    // Answer right away when flags are in, so the callback below only ever fires asynchronously.
    if (this.posthog.featureFlags?.hasLoadedFlags) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        unsubscribe();
        resolve(false);
      }, timeoutMs);

      const unsubscribe = this.posthog.onFeatureFlags((_flags, _variants, context?: { errorsLoading?: boolean }) => {
        clearTimeout(timer);
        unsubscribe();
        resolve(!context?.errorsLoading);
      });
    });
  }

  isLoaded(): boolean {
    return isPlatformBrowser(this.platformId) && isPostHogInitialized();
  }

  identify(userId: string | number, properties?: Record<string, any>) {
    if (!userId) {
      return;
    }

    if (this.isLoaded()) {
      const identifyProperties = this.removeEmptyProperties(properties || {});
      const identifyKey = this.getIdentifyKey(String(userId), identifyProperties);

      if (identifyKey === this._lastIdentifyKey) {
        return;
      }

      try {
        this.posthog.identify(String(userId), identifyProperties);
        this._lastIdentifyKey = identifyKey;
      } catch (e) {
        console.warn('PostHog identify failed', e);
      }
    }
  }

  identifyUser(user: IUser, properties: Record<string, string | number | boolean> = {}) {
    this.identifyPayload(postHogHelper.mapUserToIdentify(user, properties));
  }

  identifyAuthPayload(payload: IAuthTrackPayload, properties: Record<string, string | number | boolean> = {}) {
    this.identifyPayload(postHogHelper.mapAuthToIdentify(payload, properties));
  }

  identifyPayment(userData: IUserDataPaymentInfo, properties: Record<string, string | number | boolean> = {}) {
    this.identifyPayload(postHogHelper.mapPaymentToIdentify(userData, properties));
  }

  identifyOrderPurchase(order: IOrder) {
    this.identifyPayload(postHogHelper.mapOrderPurchaseToIdentify(order));
  }

  setPersonPropertiesForFlags(properties: Record<string, any>) {
    if (this.isLoaded() && typeof this.posthog.setPersonPropertiesForFlags === 'function') {
      try {
        this.posthog.setPersonPropertiesForFlags(properties);
      } catch (e) {
        console.warn('PostHog setPersonPropertiesForFlags failed', e);
      }
    }
  }

  capture(eventName: string, properties?: Record<string, any>) {
    if (this.isLoaded()) {
      try {
        this.posthog.capture(eventName, properties);
      } catch (e) {
        console.warn('PostHog capture failed', e);
      }
    }
  }

  group(groupType: string, groupKey: string, groupProperties?: Record<string, any>) {
    if (this.isLoaded() && typeof this.posthog.group === 'function') {
      try {
        this.posthog.group(groupType, groupKey, groupProperties);
      } catch (e) {
        console.warn('PostHog group failed', e);
      }
    }
  }

  reset() {
    this._lastIdentifyKey = '';

    if (this.isLoaded()) {
      try {
        this.posthog.reset();
      } catch (e) {
        console.warn('PostHog reset failed', e);
      }
    }
  }

  getFeatureFlag(flag: string): any {
    if (this.isLoaded()) {
      return this.posthog.getFeatureFlag(flag);
    }
    return undefined;
  }

  isFeatureEnabled(flag: string): boolean {
    if (this.isLoaded()) {
      return this.posthog.isFeatureEnabled(flag) === true;
    }
    return false;
  }

  onFeatureFlags(callback: (flags: string[], flagsVariants: Record<string, string | boolean>) => void) {
    if (this.isLoaded() && typeof this.posthog.onFeatureFlags === 'function') {
      try {
        this.posthog.onFeatureFlags(callback);
      } catch (e) {
        console.warn('PostHog onFeatureFlags failed', e);
      }
    }
  }

  reloadFeatureFlags() {
    if (this.isLoaded() && typeof this.posthog.reloadFeatureFlags === 'function') {
      try {
        this.posthog.reloadFeatureFlags();
      } catch (e) {
        console.warn('PostHog reloadFeatureFlags failed', e);
      }
    }
  }

  getFeatureFlagResult(flag: string, options?: FeatureFlagOptions): FeatureFlagResult | undefined {
    if (this.isLoaded() && typeof this.posthog.getFeatureFlagResult === 'function') {
      try {
        return this.posthog.getFeatureFlagResult(flag, options);
      } catch (e) {
        console.warn('PostHog getFeatureFlagResult failed', e);
      }
    }
    return undefined;
  }

  private identifyPayload(payload: IPostHogIdentifyPayload | null) {
    if (!payload) {
      return;
    }

    this.identify(payload.distinctId, payload.properties);
  }

  private removeEmptyProperties(properties: Record<string, any>): Record<string, string | number | boolean> {
    return Object.keys(properties).reduce(
      (acc, key) => {
        const value = properties[key];
        return value === undefined || value === null || value === '' ? acc : { ...acc, [key]: value };
      },
      {} as Record<string, string | number | boolean>,
    );
  }

  private getIdentifyKey(userId: string, properties: Record<string, string | number | boolean>): string {
    return JSON.stringify({
      userId,
      properties: Object.keys(properties)
        .sort()
        .reduce(
          (acc, key) => ({
            ...acc,
            [key]: properties[key],
          }),
          {} as Record<string, string | number | boolean>,
        ),
    });
  }
}
